import React, { useMemo } from 'react';
import { DataTable, Text, TooltipTrigger } from '@spark';
import {
  getCountsCellStyle,
  getMaxByPredicate,
  isValidMatrix,
} from './confusionMatrixUtils';

function computeTotals(matrix, nRows, nCols) {
  const rowTotals = new Array(nRows).fill(0);
  const colTotals = new Array(nCols).fill(0);
  for (let i = 0; i < nRows; i++) {
    for (let j = 0; j < nCols; j++) {
      const v = matrix?.[i]?.[j] ?? 0;
      rowTotals[i] += v;
      colTotals[j] += v;
    }
  }
  return { rowTotals, colTotals };
}

function HeatmapCell({ value, isDiagonal, maxDiagonal, maxOffDiagonal, palette, tooltipText }) {
  const styleObj = getCountsCellStyle({
    value,
    isDiagonal,
    maxDiagonal,
    maxOffDiagonal,
    colors: palette,
  });

  return (
    <TooltipTrigger text={tooltipText} placement="top">
      <div
        className="cm-heatmap-cell"
        style={{
          background: styleObj.background,
          color: styleObj.color,
          fontWeight: styleObj.fontWeight,
        }}
        aria-label={tooltipText}
      >
        {String(value)}
      </div>
    </TooltipTrigger>
  );
}

export default function ConfusionMatrixHeatmap({
  labels,
  columnLabels,
  matrix,
  palette,
  tooltipModelA,
  rowAxisLabel = 'Actual',
  colAxisLabel = 'Predicted',
}) {
  const rowLabels = labels;
  const colLabels = columnLabels || labels;

  const isDiagonalCell = (i, j) => String(rowLabels?.[i]) === String(colLabels?.[j]);

  const maxDiagonal = useMemo(
    () => getMaxByPredicate(matrix, (i, j) => isDiagonalCell(i, j)),
    [matrix, rowLabels, colLabels],
  );
  const maxOffDiagonal = useMemo(
    () => getMaxByPredicate(matrix, (i, j) => !isDiagonalCell(i, j)),
    [matrix, rowLabels, colLabels],
  );

  const { rowTotals, colTotals } = useMemo(
    () => (
      isValidMatrix(matrix)
        ? computeTotals(matrix, rowLabels.length, colLabels.length)
        : { rowTotals: [], colTotals: [] }
    ),
    [matrix, rowLabels.length, colLabels.length],
  );

  const columns = useMemo(() => {
    if (!Array.isArray(rowLabels) || rowLabels.length === 0 || !Array.isArray(colLabels) || colLabels.length === 0) {
      return [];
    }

    const cols = [
      {
        key: 'label',
        dataIndex: 'label',
        width: 110,
        title: (
          <div className="cm-corner-title">
            <div>{colAxisLabel} →</div>
            <div>{rowAxisLabel} ↓</div>
          </div>
        ),
        render: (value, record) => (
          <TooltipTrigger text={`${String(value)} - Total: ${rowTotals[record.rowIndex]?.toLocaleString()}`} placement="left">
            <div className="cm-row-label">
              <Text variant="detail-regular" color="primary" as="span">
                {String(value)}
              </Text>
            </div>
          </TooltipTrigger>
        ),
      },
    ];

    colLabels.forEach((lab, j) => {
      cols.push({
        key: `col-${j}`,
        dataIndex: `c${j}`,
        flex: 1,
        minWidth: 0,
        title: (
          <TooltipTrigger text={`${String(lab)} - Total: ${colTotals[j]?.toLocaleString()}`} placement="top">
            <div className="cm-col-label">
              {String(lab)}
            </div>
          </TooltipTrigger>
        ),
        render: (value, record) => (
          <HeatmapCell
            value={value ?? 0}
            isDiagonal={String(record.label) === String(lab)}
            maxDiagonal={maxDiagonal}
            maxOffDiagonal={maxOffDiagonal}
            palette={palette}
            tooltipText={`${tooltipModelA || 'Model'}: ${rowAxisLabel}: ${String(record.label)}, ${colAxisLabel}: ${String(lab)}, Count: ${String(value ?? 0)}`}
          />
        ),
      });
    });

    return cols;
  }, [rowLabels, colLabels, rowTotals, colTotals, maxDiagonal, maxOffDiagonal, palette, tooltipModelA, rowAxisLabel, colAxisLabel]);

  const dataSource = useMemo(() => {
    if (!Array.isArray(rowLabels) || rowLabels.length === 0 || !Array.isArray(colLabels) || colLabels.length === 0 || !isValidMatrix(matrix)) {
      return [];
    }
    return rowLabels.map((rowLabel, i) => {
      const row = {
        key: `row-${i}`,
        label: rowLabel,
        rowIndex: i,
      };
      for (let j = 0; j < colLabels.length; j += 1) {
        row[`c${j}`] = matrix?.[i]?.[j] ?? 0;
      }
      return row;
    });
  }, [rowLabels, colLabels, matrix]);

  if (!Array.isArray(rowLabels) || rowLabels.length === 0 || !Array.isArray(colLabels) || colLabels.length === 0 || !isValidMatrix(matrix)) {
    return (
      <Text variant="detail-regular" color="secondary" style={{ padding: 16 }}>
        No confusion matrix available.
      </Text>
    );
  }

  return (
    <div className="cm-datatable" data-cm-row-axis={rowAxisLabel} data-cm-col-axis={colAxisLabel}>
      <DataTable
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        pagination={null}
        emptyText="No confusion matrix available."
        disableHoverBackground
      />
    </div>
  );
}
